import { css, styled } from "../../../../styles";

export const RuleRowContainer = styled("div", {
  display: "flex",
  gap: "22px",
  // Delete button sits on the right, vertically centered across the whole
  // (possibly wrapped) block of fields.
  alignItems: "center",
});

// Holds the rule's field rows; stacks them vertically while the delete button
// stays out on the right.
export const FieldsWrap = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "22px",
  alignItems: "flex-start",
  flex: 1,
  minWidth: 0,
});

// A single line of fields; wraps if the viewport is too narrow to hold them.
export const FieldsRow = styled("div", {
  display: "flex",
  gap: "22px",
  alignItems: "flex-start",
  flexWrap: "wrap",
  width: "100%",
  minWidth: 0,
});

export const FieldContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  minWidth: 0,

  variants: {
    size: {
      column: { width: "189px" },
      sex: { width: "88px" },
      label: { width: "140px" },
      value: { width: "88px" },
      boundary: { width: "168px" },
      color: { width: "180px" },
    },
  },
});

// A rule boundary: comparator dropdown (fixed) + value input (fills the rest).
export const BoundaryRow = styled("div", {
  display: "flex",
  gap: "8px",
  alignItems: "center",
  width: "100%",

  // Only the boundary's own number input flexes; the SelectDropdown keeps its
  // fixed width. Direct-child selector avoids hitting antd Select's inner input.
  "& > input": {
    flex: 1,
    width: "auto",
    minWidth: 0,
  },
});

export const FieldLabelRow = styled("div", {
  display: "flex",
  gap: "4px",
  alignItems: "center",
  height: "16px",
});

export const FieldLabel = styled("label", {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "16px",
  color: "$charcoal",
});

export const RequiredMark = styled("span", {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "16px",
  color: "$charcoal",
});

export const ErrorText = styled("span", {
  fontSize: "11px",
  color: "$error",
  fontFamily: "'Open Sans', sans-serif",
});

export const OverlapBanner = styled("div", {
  width: "100%",
  marginTop: "4px",
  padding: "8px 12px",
  borderRadius: "6px",
  backgroundColor: "rgba(225, 29, 63, 0.08)",
  color: "$error",
  fontSize: "12px",
  lineHeight: "18px",
  fontFamily: "'Open Sans', sans-serif",
  whiteSpace: "pre-line",
});

export const NumberInput = styled("input", {
  display: "block",
  height: "42px",
  width: "100%",
  padding: "0 16px",
  border: "1.2px solid $gray-50",
  borderRadius: "6px",
  fontFamily: "'Open Sans', sans-serif",
  fontSize: "14px",
  lineHeight: "22px",
  color: "$black",
  backgroundColor: "$white",
  boxSizing: "border-box",
  outline: "none",
  appearance: "none",
  MozAppearance: "textfield",

  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },

  "&:focus": {
    borderColor: "$primary-30",
  },

  variants: {
    hasError: {
      true: { borderColor: "$error" },
    },
  },
});

export const LabelInput = styled("input", {
  display: "block",
  height: "42px",
  width: "100%",
  padding: "0 16px",
  border: "1.2px solid $gray-50",
  borderRadius: "6px",
  fontFamily: "'Open Sans', sans-serif",
  fontSize: "14px",
  lineHeight: "22px",
  color: "$black",
  backgroundColor: "$white",
  boxSizing: "border-box",
  outline: "none",

  "&:focus": {
    borderColor: "$primary-30",
  },

  variants: {
    hasError: {
      true: { borderColor: "$error" },
    },
  },
});

export const DeleteButton = styled("button", {
  all: "unset",
  cursor: "pointer",
  height: "28px",
  width: "28px",
  flexShrink: 0,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "$gray-80",

  "&:hover": {
    backgroundColor: "$gray-40",
    color: "$error",
  },

  svg: {
    width: "14px",
    height: "14px",
  },
});

// All antd-internal selectors live on the wrapper itself so they cascade to
// the descendant `<ColorPicker>` — keeps us on Stitches `styled()` per the
// project styling guideline (no top-level `css()` for new code).
export const ColorPickerWrapper = styled("div", {
  width: "100%",

  ".ant-color-picker-trigger": {
    minHeight: "42px",
    width: "100%",
    justifyContent: "flex-start",
    borderColor: "$gray-50 !important",
    borderWidth: "1.2px !important",
    borderRadius: "6px !important",
    padding: "0 12px !important",

    "&.ant-color-picker-trigger-active": {
      borderColor: "var(--colors-portal-dropdown-selected-border) !important",
    },

    "&:hover": {
      borderColor: "var(--colors-portal-dropdown-selected-border) !important",
    },
  },

  ".ant-color-picker-color-block": {
    width: "22px !important",
    height: "22px !important",
    borderRadius: "4px !important",
    alignSelf: "center",
    marginRight: "8px",
  },

  ".ant-color-picker-trigger-text": {
    fontFamily: "$openSans",
    fontSize: "14px",
    color: "$black",
  },
});

// Popover root for the color picker is portalled to body by antd, so it
// can't be styled via a parent `styled()` wrapper. `css()` is the only way
// to target the portalled element here. Z-index has to clear the
// BuilderLayout container (2001) so the panel is clickable.
export const colorPickerRootClassName = css({
  zIndex: 3001,
});
